import { useEffect, useRef, useState } from 'react'
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'
import { Button } from '@/components/button/Button'
import { SearchField } from '@/components/search-field/SearchField'
import { kExternalStorageProviderDisplayName } from '@/constants/constants-external-storage'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { ConnectedAccount } from '@/settings/schemas/external-storage-schema'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch } from '@/store/store-hooks'
import { ExternalFolderList } from '@/user_files/external-storage/components/ExternalFolderList'
import { getExternalAccountFiles } from '@/user_files/external-storage/fetch/get-account-files'
import { postImportFolder } from '@/user_files/external-storage/fetch/post-import-folder'
import { ExternalFileItem, ImportFolderResponse } from '@/user_files/external-storage/schemas'
import { Modal, ModalClose, ModalTitle, ModalDescription } from '@/components/modal/Modal'
import { ModalContainer } from '@/components/modal/ModalContainer'

export interface SelectExternalFolderModalSuccess {
  account: ConnectedAccount
  folder: ExternalFileItem
  response: ImportFolderResponse
}

interface ExternalFolderBreadcrumbEntry {
  id: string
  name: string
}

interface SelectExternalFolderModalProps {
  account: ConnectedAccount
  currentExternalFolderId?: string | null
  matterId: string
  onOpenChange: (open: boolean) => void
  onSuccess?: (result: SelectExternalFolderModalSuccess) => void
  open: boolean
  replaceExistingMapping?: boolean
}

export function SelectExternalFolderModal({
  account,
  currentExternalFolderId,
  matterId,
  onOpenChange,
  onSuccess,
  open,
  replaceExistingMapping = false,
}: SelectExternalFolderModalProps) {
  const dispatch = useAppDispatch()
  const [items, setItems] = useState<ExternalFileItem[]>([])
  const [breadcrumbs, setBreadcrumbs] = useState<ExternalFolderBreadcrumbEntry[]>([])
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const didPreselectCurrentFolderRef = useRef(false)

  const browsedFolderId = breadcrumbs.at(-1)?.id
  const providerLabel = kExternalStorageProviderDisplayName[account.provider]

  useEffect(() => {
    if (!open) return

    let active = true

    setLoadingFiles(true)
    setError(null)
    if (!didPreselectCurrentFolderRef.current && !browsedFolderId) {
      setSelectedFolderId(currentExternalFolderId ?? null)
      didPreselectCurrentFolderRef.current = true
    }

    getExternalAccountFiles(account.id, browsedFolderId)
      .then((response) => {
        if (!active) return
        setItems(response.items)
      })
      .catch(() => {
        if (!active) return
        setError('Failed to load folders.')
      })
      .finally(() => {
        if (!active) return
        setLoadingFiles(false)
      })

    return () => {
      active = false
    }
  }, [account.id, browsedFolderId, currentExternalFolderId, open])

  const reset = () => {
    setItems([])
    setBreadcrumbs([])
    setSelectedFolderId(null)
    didPreselectCurrentFolderRef.current = false
    setSearch('')
    setError(null)
    setIsImporting(false)
  }

  const handleClose = () => {
    reset()
    onOpenChange(false)
  }

  const handleDrillIn = (folder: ExternalFileItem) => {
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }])
    setSelectedFolderId(null)
    setSearch('')
  }

  const handleBreadcrumbNav = (index: number) => {
    setBreadcrumbs((prev) => prev.slice(0, index + 1))
    setSelectedFolderId(null)
    setSearch('')
  }

  const handleRootBreadcrumbNav = () => {
    setBreadcrumbs([])
    setSelectedFolderId(null)
    setSearch('')
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
  }

  const handleConnect = async () => {
    if (!selectedFolder || selectedFolderIsUnavailable) return

    setIsImporting(true)
    setError(null)

    try {
      const response = await postImportFolder({
        accountId: account.id,
        externalFolderId: selectedFolder.id,
        matterId,
        replaceExistingMapping,
      })
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: `Syncing folders and files from ${providerLabel}. You can keep working.`,
          durationMs: kToastDurationMediumMs,
        })
      )
      handleClose()
      onSuccess?.({ account, folder: selectedFolder, response })
    } catch {
      setError('Failed to connect folder. Please try again.')
      setIsImporting(false)
    }
  }

  const folders = items.filter((item) => item.is_folder)
  const filteredFolders = folders.filter((folder) => folder.name.toLowerCase().includes(search.toLowerCase()))
  const unavailableFolderIds = new Set(
    folders.filter((folder) => folder.external_sync_matter_id != null && folder.external_sync_matter_id !== matterId).map((folder) => folder.id)
  )
  const selectedFolder = selectedFolderId ? filteredFolders.find((item) => item.id === selectedFolderId) ?? null : null
  const selectedFolderIsCurrent = replaceExistingMapping && selectedFolder?.id === currentExternalFolderId
  const selectedFolderIsUnavailable = selectedFolder ? unavailableFolderIds.has(selectedFolder.id) : false
  const breadcrumbItems: BreadcrumbItemType[] = [
    {
      id: 'all-folders',
      label: 'All Folders',
      ...(breadcrumbs.length > 0 ? { onClick: handleRootBreadcrumbNav } : {}),
    },
    ...breadcrumbs.map((crumb, index) => ({
      id: crumb.id,
      label: crumb.name,
      ...(index < breadcrumbs.length - 1 ? { onClick: () => handleBreadcrumbNav(index) } : {}),
    })),
  ]

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose()
      }}
    >
      <ModalContainer width="md" height="max-full">
        <div className="px-6 pb-4 pt-6">
          <ModalTitle>Select {providerLabel} Folder</ModalTitle>
          <ModalDescription className="mt-1.5 text-base text-brand-neutral-950">Choose a {providerLabel} folder to sync with this matter.</ModalDescription>
        </div>

        <div className="min-h-0 flex-1">
          <div className="px-4 pb-2">
            <SearchField value={search} onChange={handleSearchChange} placeholder="Search Folders" autoFocus />
          </div>
          <div className="px-4 py-2">
            <Breadcrumbs items={breadcrumbItems} maxItems={3} persistFirstItem />
          </div>
          <ExternalFolderList
            error={error}
            folders={filteredFolders}
            loading={loadingFiles}
            selectedFolderId={selectedFolderId}
            unavailableFolderIds={unavailableFolderIds}
            onDrillIn={handleDrillIn}
            onSelectFolder={setSelectedFolderId}
          />
        </div>

        <div className="flex items-center justify-end gap-4 border-t border-brand-neutral-200 bg-white rounded-b-2xl p-4">
          <ModalClose asChild>
            <Button type="button" variant="outlined" color="primary" size="md" className="min-w-[90px]">
              Cancel
            </Button>
          </ModalClose>
          <Button
            type="button"
            disabled={!selectedFolder || selectedFolderIsCurrent || selectedFolderIsUnavailable || isImporting}
            onClick={handleConnect}
            loading={isImporting}
            size="md"
            className="min-w-[120px]"
          >
            {replaceExistingMapping ? 'Change Folder' : 'Connect Folder'}
          </Button>
        </div>
      </ModalContainer>
    </Modal>
  )
}
